import { USER_ROLES } from '../constants';
import { ADDONS } from '../constants/addons';
import {
  isRequired,
  isSame,
  isNumber,
  isUniqArray,
  isArraySizeAtLeast,
  isNumberArray,
  type Validation,
} from './utils';

interface CollectionRef {
  id: string | number;
  availableForAll?: boolean;
  availableForAllReadonly?: boolean;
}

interface CustomerRef {
  id: string | number;
  relateSalespersonId?: string | number;
  collectionRestrictionIds: Array<string | number>;
  name: string;
}

interface SalespersonRef {
  id: string | number;
  collectionRestrictionIds: Array<string | number>;
  name: string;
}

interface UserFormValues {
  [key: string]: unknown;
  availableCollections: CollectionRef[];
  availableCustomers: CustomerRef[];
  availableSalesperson: SalespersonRef[];
  salespersonCustomerIds: Array<string | number>;
  collectionRestrictionIds: Array<string | number>;
}

const validations: Validation<UserFormValues>[] = [
  ({ username }) => isRequired(username) || { username: 'User name is required' },
  ({ password, passwordResetByUser }) => (
    (passwordResetByUser === true)
    || isRequired(password)
    || { password: 'Password is required' }
  ),

  ({ name }) => isRequired(name) || { name: 'Name is required' },
  ({ email }) => isRequired(email) || { email: 'Email is required' },
  ({ phone }) => isRequired(phone) || { phone: 'Phone is required' },
  ({ address }) => isRequired(address) || { address: 'Address is required' },
  ({
    disableShippingInfo,
    shippingName,
  }) => (
    isSame(disableShippingInfo, true)
    || isRequired(shippingName)
    || { shippingName: 'Name of shipping info is required.' }
  ),
  ({
    disableShippingInfo,
    shippingEmail,
  }) => (
    isSame(disableShippingInfo, true)
    || isRequired(shippingEmail)
    || { shippingEmail: 'Email of shipping info is required.' }
  ),
  ({
    disableShippingInfo,
    shippingPhone,
  }) => (
    isSame(disableShippingInfo, true)
    || isRequired(shippingPhone)
    || { shippingPhone: 'Phone of shipping info is required.' }
  ),
  ({
    disableShippingInfo,
    shippingAddress,
  }) => (
    isSame(disableShippingInfo, true)
    || isRequired(shippingAddress)
    || { shippingAddress: 'Address of shipping info is required.' }
  ),

  ({
    disableBillingInfo,
    billingName,
  }) => (
    isSame(disableBillingInfo, true)
    || isRequired(billingName)
    || { billingName: 'Name of billing info is required.' }
  ),
  ({
    disableBillingInfo,
    billingEmail,
  }) => (
    isSame(disableBillingInfo, true)
    || isRequired(billingEmail)
    || { billingEmail: 'Email of billing info is required.' }
  ),
  ({
    disableBillingInfo,
    billingPhone,
  }) => (
    isSame(disableBillingInfo, true)
    || isRequired(billingPhone)
    || { billingPhone: 'Phone of billing info is required.' }
  ),
  ({
    disableBillingInfo,
    billingAddress,
  }) => (
    isSame(disableBillingInfo, true)
    || isRequired(billingAddress)
    || { billingAddress: 'Address of billing info is required.' }
  ),

  // admin and staff dont require collectionRestrictions
  ({ collectionRestrictionIds }) => {
    // collectionRestrictionIds should be an numeric array
    if (isArraySizeAtLeast(collectionRestrictionIds, 0)
      && isNumberArray(collectionRestrictionIds)
      && isUniqArray(collectionRestrictionIds)) {
      return true;
    }
    return {
      collectionRestrictionIds: 'Collections restrictions should be a valid numeric array.',
    };
  },
  ({ role }) => isRequired(role) || { role: 'Role is required.' },

  ({
    id: _id,
    availableCollections,
    availableCustomers,
    availableSalesperson,
    role,
    salespersonCustomerIds,
    collectionRestrictionIds,
  }, subscriptions) => {
    if (!subscriptions.available[ADDONS.SALESPERSON]) {
      return true;
    }

    if (role !== USER_ROLES.SALESPERSON) {
      return true;
    }

    // https://github.com/ScenK/BWM/issues/163
    // Assign/Re-assign customers to a salesperson,
    // if the customer's collection restrictions are
    // a subset of the salesperson's collection restrictions
    const collectionForAllIds = availableCollections
      .filter((c) => (c.availableForAll || c.availableForAllReadonly))
      .map((c) => c.id);
    // for easily returning the validation result use the for loop
    for (let i = 0; i < salespersonCustomerIds.length; i += 1) {
      const cid = salespersonCustomerIds[i];
      const customer = availableCustomers.find((c) => c.id === cid);
      if (!customer) {
        return { salespersonCustomerIds: `not found customer id: ${cid}` };
      }

      const existsSalesperson = availableSalesperson.some(
        (sp) => sp.id === customer.relateSalespersonId,
      );
      if (existsSalesperson) {
        return { salespersonCustomerIds: 'customer already assigned to the other salesperson' };
      }

      // remove for all collection from collectionRestrictionIds
      const pureSalespersonCollectionIds = collectionRestrictionIds
        .filter((cid) => !collectionForAllIds.includes(cid));

      const pureCustomerCollectionIds = customer.collectionRestrictionIds
        .filter((cid) => !collectionForAllIds.includes(cid));

      const valid = !pureCustomerCollectionIds
        .some((ccid) => !pureSalespersonCollectionIds.includes(ccid));

      if (!valid) {
        return { salespersonCustomerIds: `Collection restrictions conflict for customer: ${customer.name}` };
      }
    }
    return true;
  },

  ({
    availableCollections,
    availableSalesperson,
    role,
    relatedSalespersonId,
    collectionRestrictionIds,
  }, subscriptions) => {
    if (!subscriptions.available[ADDONS.SALESPERSON]) {
      return true;
    }

    if (role !== USER_ROLES.CUSTOMER) {
      return true;
    }

    if (!relatedSalespersonId) {
      return true;
    }

    const salesperson = availableSalesperson.find((s) => s.id === relatedSalespersonId);
    if (!salesperson) {
      return { relatedSalespersonId: `not found salesperson id: ${relatedSalespersonId as string | number}` };
    }

    const collectionForAllIds = availableCollections
      .filter((c) => (c.availableForAll || c.availableForAllReadonly))
      .map((c) => c.id);
    // remove for all collection from collectionRestrictionIds
    const pureSalespersonCollectionIds = salesperson.collectionRestrictionIds
      .filter((cid) => !collectionForAllIds.includes(cid));

    const pureCustomerCollectionIds = collectionRestrictionIds
      .filter((cid) => !collectionForAllIds.includes(cid));

    const valid = !pureCustomerCollectionIds
      .some((ccid) => !pureSalespersonCollectionIds.includes(ccid));

    if (!valid) {
      return { relatedSalespersonId: `invalid salesperson ${salesperson.name}` };
    }

    return true;
  },

  ({ role, sampleItemsPercent }) => (role !== USER_ROLES.SALESPERSON || isNumber(sampleItemsPercent))
    || { sampleItemsPercent: 'Sample item commission percentage is required.' },
  ({ role, reorderItemsPercent }) => (role !== USER_ROLES.SALESPERSON || isNumber(reorderItemsPercent))
    || { reorderItemsPercent: 'Reorder item commission percentage is required.' },
];

const KEYS = [
  'username',
  'password',

  'name',
  'email',
  'phone',
  'address',

  'disableShippingInfo',
  'shippingName',
  'shippingEmail',
  'shippingPhone',
  'shippingAddress',

  'disableBillingInfo',
  'billingName',
  'billingEmail',
  'billingPhone',
  'billingAddress',

  'relatedSalespersonId',
  'sampleItemsPercent',
  'reorderItemsPercent',

  'collectionRestrictions',
  'role',
];

export {
  validations,
  KEYS,
};
